// SPDX-License-Identifier: BUSL-1.1

/**
 * GitHubSettingsPage — GitHub organization connection.
 *
 * Path: /github-settings
 * Auth: sessionAuth via RequireAuth in app.tsx
 *
 * Shows GitHub App installation status and allows connecting/disconnecting
 * the build.ceo org from a GitHub organization account.
 *
 * Uses the GitHub App install flow:
 *   - GET    /api/auth/orgs/:slug/github/installation   (status)
 *   - POST   /api/orgs/:slug/github/wizard/connect      (start install, returns connectUrl)
 *   - DELETE /api/auth/orgs/:slug/github/installation   (disconnect)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GitFork, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api-client';
import { useCurrentOrg } from '@/contexts/org-context';
import { showSuccess, showApiError } from '@/lib/toast';

interface InstallationStatus {
  installed: boolean;
  installationId?: number;
  targetLogin?: string;
  createdAt?: string;
  /** Present when installed=false due to auth failure (401) */
  reason?: 'unauthorized';
}

async function fetchInstallationStatus(slug: string): Promise<InstallationStatus> {
  try {
    // githubAuth router mounted at /api/auth with route /orgs/:slug/github/installation
    // Full path: /api/auth/orgs/:slug/github/installation
    // noRedirect: true prevents 401 from logging out the user - we handle it gracefully
    return await apiFetch<InstallationStatus>(`/auth/orgs/${slug}/github/installation`, {
      noRedirect: true,
    });
  } catch (err) {
    // If still unauthorized after JWT refresh attempt, return not-connected state
    // instead of showing error — the page should show "Not connected" not an error.
    if (err instanceof Error && err.message.includes('Unauthorized')) {
      return { installed: false, reason: 'unauthorized' };
    }
    throw err;
  }
}

async function connectGitHub(slug: string): Promise<{ connectUrl: string }> {
  // noRedirect: true prevents 401 from logging out user - show error instead
  return apiFetch<{ connectUrl: string }>(`/orgs/${slug}/github/wizard/connect`, {
    method: 'POST',
    noRedirect: true,
  });
}

async function disconnectGitHub(slug: string): Promise<void> {
  // githubAuth router mounted at /api/auth with route /orgs/:slug/github/installation
  // noRedirect: true prevents 401 from logging out - show error instead
  return apiFetch(`/auth/orgs/${slug}/github/installation`, { method: 'DELETE', noRedirect: true });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function GitHubSettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrg } = useCurrentOrg();
  const slug = currentOrg?.slug ?? '';

  const [disconnecting, setDisconnecting] = useState(false);
  const [prevInstalled, setPrevInstalled] = useState<boolean | undefined>(undefined);

  const statusQuery = useQuery({
    queryKey: ['github-installation', slug],
    queryFn: () => fetchInstallationStatus(slug),
    enabled: !!slug,
    staleTime: 30_000,
  });

  // Show success toast when GitHub gets connected (e.g. returning from install)
  useEffect(() => {
    if (statusQuery.data?.installed && prevInstalled === false) {
      showSuccess(`GitHub connected to @${statusQuery.data.targetLogin || slug}`);
    }
    if (statusQuery.data !== undefined) {
      setPrevInstalled(statusQuery.data.installed);
    }
  }, [statusQuery.data?.installed, prevInstalled, slug]);

  const connectMutation = useMutation({
    mutationFn: () => connectGitHub(slug),
    onSuccess: (data) => {
      // Redirect user to GitHub App install page
      window.location.href = data.connectUrl;
    },
    onError: (err) => showApiError(err),
  });

  async function handleDisconnect() {
    if (!confirm('Disconnect GitHub? This will stop syncing activity from this organization.'))
      return;
    setDisconnecting(true);
    try {
      await disconnectGitHub(slug);
      showSuccess('GitHub disconnected');
      queryClient.invalidateQueries({ queryKey: ['github-installation', slug] });
    } catch (err) {
      showApiError(err);
    } finally {
      setDisconnecting(false);
    }
  }

  if (!currentOrg) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <p className="text-neutral-500">No organization selected.</p>
      </div>
    );
  }

  const { installed, installationId, targetLogin, createdAt } = statusQuery.data ?? {
    installed: false,
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-100">GitHub Organization</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Connect your GitHub organization to sync activity, repos, and events into the unified
          timeline.
        </p>
      </div>

      {/* Not connected state */}
      {!installed && (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <GitFork className="size-4" />
              Not connected
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-neutral-400">
              Install the build.ceo GitHub App to link this organization with your GitHub
              organization account. This enables the unified timeline showing GitHub events
              alongside agent activity.
            </p>

            {/* How it works — shown before install so users know what they're agreeing to */}
            <Card className="border-blue-900/40 bg-blue-950/10">
              <CardContent className="p-4">
                <h2 className="mb-2 text-sm font-semibold text-blue-400">How it works</h2>
                <ol className="flex list-inside list-decimal flex-col gap-1.5 text-xs text-neutral-400">
                  <li>You install the build.ceo GitHub App on your GitHub org</li>
                  <li>Activity from this GitHub org appears in the unified timeline</li>
                  <li>Push events, PRs, reviews, and releases are correlated with agent work</li>
                  <li>Disconnect at any time — no data is deleted from GitHub</li>
                </ol>
              </CardContent>
            </Card>
            {statusQuery.isLoading ? (
              <Skeleton className="h-10 w-40" />
            ) : statusQuery.isError ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-red-400">
                  Failed to check GitHub status. The GitHub integration may not be configured yet.
                </p>
                <p className="text-xs text-neutral-500">
                  Contact your admin to set up the GitHub App.
                </p>
              </div>
            ) : (
              <Button
                className="w-fit bg-[#238636] text-white hover:bg-[#2ea043]"
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
              >
                <GitFork className="mr-2 size-4" />
                {connectMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" /> Preparing...
                  </>
                ) : (
                  'Connect GitHub'
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Connected state */}
      {installed && (
        <>
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <GitFork className="size-4" />
                Connected to @{targetLogin}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <Badge variant="default" className="bg-green-900/40 text-green-400">
                  Connected
                </Badge>
                {installationId && (
                  <span className="text-xs text-neutral-500">Installation #{installationId}</span>
                )}
              </div>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <dt className="text-neutral-500">Organization</dt>
                <dd className="text-neutral-200">@{targetLogin}</dd>
                <dt className="text-neutral-500">Connected</dt>
                <dd className="text-neutral-200">
                  {createdAt ? formatDate(createdAt) : 'Unknown'}
                </dd>
              </dl>

              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/timeline')}>
                  View Timeline
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-900/50 text-red-400 hover:bg-red-950/30"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* How it works */}
          <Card className="max-w-xl border-blue-900/40 bg-blue-950/10">
            <CardContent className="p-4">
              <h2 className="mb-2 text-sm font-semibold text-blue-400">How it works</h2>
              <ol className="flex list-inside list-decimal flex-col gap-1.5 text-xs text-neutral-400">
                <li>The build.ceo GitHub App is installed on @{targetLogin}</li>
                <li>Activity from this GitHub org appears in the unified timeline</li>
                <li>Push events, PRs, reviews, and releases are correlated with agent work</li>
                <li>Disconnect at any time — no data is deleted from GitHub</li>
              </ol>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
